/**
 * Reliability Alerts Page - Phase 15
 * 
 * Alert center for managing reliability alerts.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Clock,
  ArrowUp,
  RefreshCw,
  Filter,
  ChevronRight,
  Eye,
  Check,
  X,
  MessageSquare,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Sheet, 
  SheetContent, 
  SheetDescription, 
  SheetHeader, 
  SheetTitle 
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import {
  getActiveAlerts,
  getAlertSummary,
  getAlertById
} from '@/lib/reliabilityAlerts/alertStore';
import {
  acknowledgeAlert,
  escalateAlert,
  resolveAlert,
  dismissAlert,
  addAlertNote
} from '@/lib/reliabilityAlerts/alertWorkflow';
import { runEvaluationCycle } from '@/lib/reliabilityAlerts/alertEngine';
import {
  getEscalationGuidance,
  getSuggestedNextActions,
  getAlertCrossLinks
} from '@/lib/reliabilityAlerts/escalationRules';
import {
  ALERT_STATUS,
  ALERT_SEVERITY,
  getAlertTypeLabel,
  isAlertOpen
} from '@/lib/reliabilityAlerts/alertModel';
import { BO_BASE } from '@/utils/getBasePath';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

export default function ReliabilityAlertsPage() {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  
  const [filters, setFilters] = useState({
    status: 'open',
    severity: '',
    searchText: ''
  });
  
  const [noteText, setNoteText] = useState('');
  const [dismissNote, setDismissNote] = useState('');

  // Load alerts
  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const filterParams = {
        openOnly: filters.status === 'open',
        status: filters.status === 'open' ? undefined : filters.status || undefined,
        severity: filters.severity || undefined,
        searchText: filters.searchText || undefined
      };
      
      const [alertsData, summaryData] = await Promise.all([
        getActiveAlerts(filterParams),
        getAlertSummary({})
      ]);
      
      setAlerts(alertsData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  // Run evaluation cycle
  const handleEvaluate = async () => {
    setLoading(true);
    try {
      await runEvaluationCycle();
      await loadAlerts();
    } catch (error) {
      console.error('Evaluation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Alert actions
  const handleAcknowledge = async (alertId) => {
    await acknowledgeAlert(alertId, { userId: 'current-user' });
    await loadAlerts();
    if (selectedAlert?.alertId === alertId) {
      setSelectedAlert(await getAlertById(alertId));
    }
  };

  const handleEscalate = async (alertId) => {
    await escalateAlert(alertId, { userId: 'current-user' });
    await loadAlerts();
    if (selectedAlert?.alertId === alertId) {
      setSelectedAlert(await getAlertById(alertId));
    }
  };

  const handleResolve = async (alertId) => {
    await resolveAlert(alertId, { userId: 'current-user' });
    await loadAlerts();
    setShowDetailPanel(false);
    setSelectedAlert(null);
  };

  const handleDismiss = async (alertId) => {
    if (!dismissNote) {
      alert('Please provide a note for dismissal');
      return;
    }
    await dismissAlert(alertId, { userId: 'current-user' }, dismissNote);
    await loadAlerts();
    setShowDetailPanel(false);
    setSelectedAlert(null);
    setDismissNote('');
  };

  const handleAddNote = async (alertId) => {
    if (!noteText) return;
    await addAlertNote(alertId, noteText, { userId: 'current-user' });
    setNoteText('');
    if (selectedAlert?.alertId === alertId) {
      setSelectedAlert(await getAlertById(alertId));
    }
  };

  const openAlertDetail = (alert) => {
    setSelectedAlert(alert);
    setShowDetailPanel(true);
    setNoteText('');
    setDismissNote('');
  };

  // Severity badge
  const getSeverityBadge = (severity) => {
    const config = {
      critical: { class: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
      error: { class: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', icon: AlertTriangle },
      warning: { class: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: AlertTriangle },
      info: { class: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Bell }
    };
    const c = config[severity] || config.info;
    const Icon = c.icon;
    return (
      <Badge className={c.class}>
        <Icon className="h-3 w-3 mr-1" />
        {severity}
      </Badge>
    );
  };

  // Status badge
  const getStatusBadge = (status) => {
    const config = {
      open: 'bg-red-100 text-red-800 dark:bg-red-900/30',
      acknowledged: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30',
      escalated: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30',
      resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30',
      dismissed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30',
      suppressed: 'bg-gray-100 text-gray-600 dark:bg-gray-900/30'
    };
    return <Badge className={config[status] || config.open}>{status}</Badge>;
  };

  const formatTime = (ts) => {
    if (!ts) return 'N/A';
    return new Date(ts).toLocaleString();
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="reliability-alerts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Reliability Alerts
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Monitor and respond to operational alerts
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleEvaluate} disabled={loading} data-testid="evaluate-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Evaluate
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card data-testid="summary-open">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.open || 0}</p>
                <p className="text-xs text-gray-500">Open</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card data-testid="summary-critical">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-600">{summary?.critical || 0}</p>
                <p className="text-xs text-gray-500">Critical</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card data-testid="summary-escalated">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <ArrowUp className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.escalated || 0}</p>
                <p className="text-xs text-gray-500">Escalated</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card data-testid="summary-unacked">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.unacknowledged || 0}</p>
                <p className="text-xs text-gray-500">Unacked</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card data-testid="summary-acknowledged">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{summary?.acknowledged || 0}</p>
                <p className="text-xs text-gray-500">Acknowledged</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card data-testid="summary-resolved">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{summary?.resolved || 0}</p>
                <p className="text-xs text-gray-500">Resolved</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search alerts..."
                value={filters.searchText}
                onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
                data-testid="search-input"
              />
            </div>
            
            <Select
              value={filters.status || 'all'}
              onValueChange={(val) => setFilters({ ...filters, status: val === 'all' ? '' : val })}
            >
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="escalated">Escalated</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={filters.severity || 'all'}
              onValueChange={(val) => setFilters({ ...filters, severity: val === 'all' ? '' : val })}
            >
              <SelectTrigger className="w-[150px]" data-testid="severity-filter">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={loadAlerts} data-testid="apply-filters-btn">
              <Filter className="h-4 w-4 mr-2" />
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alerts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Alerts ({alerts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-8 text-gray-500" data-testid="no-alerts">
              No alerts found matching filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b dark:border-gray-700">
                    <th className="text-left py-3 px-4">Severity</th>
                    <th className="text-left py-3 px-4">Alert</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Scope</th>
                    <th className="text-left py-3 px-4">Time</th>
                    <th className="text-right py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => (
                    <tr 
                      key={alert.alertId} 
                      className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      onClick={() => openAlertDetail(alert)}
                      data-testid={`alert-row-${alert.alertId}`}
                    >
                      <td className="py-3 px-4">{getSeverityBadge(alert.severity)}</td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{alert.title}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[300px]">{alert.message}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">{getStatusBadge(alert.status)}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">
                        {alert.scope?.terminalId && <div>T: {alert.scope.terminalId}</div>}
                        {alert.scope?.locationId && <div>L: {alert.scope.locationId}</div>}
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-500">
                        {formatTime(alert.createdAt)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          {alert.status === ALERT_STATUS.OPEN && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleAcknowledge(alert.alertId)}
                              data-testid={`ack-btn-${alert.alertId}`}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                          )}
                          {isAlertOpen(alert) && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleEscalate(alert.alertId)}
                              data-testid={`escalate-btn-${alert.alertId}`}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cross-links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Related Tools</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <a
              href={bo('support-diagnostics')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-diagnostics"
            >
              <span>Support Diagnostics</span>
              <ChevronRight className="h-4 w-4" />
            </a>
            <a
              href={bo('reliability-reporting')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-reliability"
            >
              <span>Reliability Report</span>
              <ChevronRight className="h-4 w-4" />
            </a>
            <a
              href={bo('incident-audit')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-incidents"
            >
              <span>Incident Audit</span>
              <ChevronRight className="h-4 w-4" />
            </a>
            <a
              href={bo('pos-reconciliation')}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              data-testid="link-pos"
            >
              <span>POS Reconciliation</span>
              <ChevronRight className="h-4 w-4" />
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Detail Panel */}
      <Sheet open={showDetailPanel} onOpenChange={setShowDetailPanel}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedAlert && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {getSeverityBadge(selectedAlert.severity)}
                  <span>{selectedAlert.title}</span>
                </SheetTitle>
                <SheetDescription>
                  {getAlertTypeLabel(selectedAlert.alertType)}
                </SheetDescription>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                {/* Status & Actions */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Status:</span>
                    {getStatusBadge(selectedAlert.status)}
                  </div>
                  
                  {isAlertOpen(selectedAlert) && (
                    <div className="flex flex-wrap gap-2">
                      {selectedAlert.status === ALERT_STATUS.OPEN && (
                        <Button 
                          size="sm" 
                          onClick={() => handleAcknowledge(selectedAlert.alertId)}
                          data-testid="detail-ack-btn"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Acknowledge
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEscalate(selectedAlert.alertId)}
                        data-testid="detail-escalate-btn"
                      >
                        <ArrowUp className="h-4 w-4 mr-1" />
                        Escalate
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleResolve(selectedAlert.alertId)}
                        data-testid="detail-resolve-btn"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Resolve
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Message */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm">{selectedAlert.message}</p>
                </div>
                
                {/* Guidance */}
                {(() => {
                  const guidance = getEscalationGuidance(selectedAlert);
                  return (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Response Guidance</h4>
                      <p className="text-xs text-gray-500">{guidance.description}</p>
                      <ul className="text-sm space-y-1">
                        {guidance.suggestedActions.slice(0, 4).map((action, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <ChevronRight className="h-4 w-4 mt-0.5 text-gray-400" />
                            <span>{action}</span>
                          </li>
                        ))}
                      </ul>
                      {guidance.escalationRequired && (
                        <Badge className="bg-purple-100 text-purple-800">
                          Escalation Recommended → {guidance.escalationTarget}
                        </Badge>
                      )}
                    </div>
                  );
                })()}
                
                {/* Quick Links */}
                {(() => {
                  const links = getAlertCrossLinks(selectedAlert);
                  return (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Quick Links</h4>
                      <div className="flex flex-wrap gap-2">
                        {links.diagnostics && (
                          <a href={links.diagnostics} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                            <ExternalLink className="h-3 w-3" />
                            Diagnostics
                          </a>
                        )}
                        {links.reliability && (
                          <a href={links.reliability} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                            <ExternalLink className="h-3 w-3" />
                            Reliability
                          </a>
                        )}
                        {links.incidentAudit && (
                          <a href={links.incidentAudit} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                            <ExternalLink className="h-3 w-3" />
                            Incidents
                          </a>
                        )}
                        {links.posReconciliation && (
                          <a href={links.posReconciliation} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                            <ExternalLink className="h-3 w-3" />
                            POS
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Timeline */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">Timeline</h4>
                  <div className="text-xs space-y-1 text-gray-500">
                    <div>Created: {formatTime(selectedAlert.createdAt)}</div>
                    {selectedAlert.acknowledgedAt && (
                      <div>Acknowledged: {formatTime(selectedAlert.acknowledgedAt)} by {selectedAlert.actorState?.acknowledgedBy}</div>
                    )}
                    {selectedAlert.escalatedAt && (
                      <div>Escalated: {formatTime(selectedAlert.escalatedAt)} by {selectedAlert.actorState?.escalatedBy}</div>
                    )}
                    {selectedAlert.resolvedAt && (
                      <div>Resolved: {formatTime(selectedAlert.resolvedAt)} by {selectedAlert.actorState?.resolvedBy}</div>
                    )}
                  </div>
                </div>
                
                {/* Manager Note */}
                {selectedAlert.managerNote && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Notes</h4>
                    <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-3 rounded whitespace-pre-wrap">
                      {selectedAlert.managerNote}
                    </pre>
                  </div>
                )}
                
                {/* Add Note */}
                {isAlertOpen(selectedAlert) && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Add Note</h4>
                    <Textarea
                      placeholder="Add a note..."
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      className="text-sm"
                      data-testid="add-note-input"
                    />
                    <Button 
                      size="sm" 
                      onClick={() => handleAddNote(selectedAlert.alertId)}
                      disabled={!noteText}
                      data-testid="add-note-btn"
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      Add Note
                    </Button>
                  </div>
                )}
                
                {/* Dismiss */}
                {isAlertOpen(selectedAlert) && (
                  <div className="space-y-2 border-t pt-4">
                    <h4 className="font-medium text-sm">Dismiss Alert</h4>
                    <Textarea
                      placeholder="Reason for dismissal (required)..."
                      value={dismissNote}
                      onChange={(e) => setDismissNote(e.target.value)}
                      className="text-sm"
                      data-testid="dismiss-note-input"
                    />
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDismiss(selectedAlert.alertId)}
                      disabled={!dismissNote}
                      data-testid="dismiss-btn"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Dismiss
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
